import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { Maybe, ServiceTypesGQL } from '@iss/libs/graphql';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  OnInit,
  Self,
  SimpleChanges,
} from '@angular/core';

import { ServiceType, ServiceTypes } from './service-type-input.types';

import { PackageCategory } from '@iss/wo-common/trade-off';
import { TypeaheadSelectComponent } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-service-type-input',
  templateUrl: './service-type-input.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
  ],
})
export class ServiceTypeInputComponent implements OnInit, OnChanges {
  private readonly query = inject(ServiceTypesGQL);

  public loading$!: Observable<boolean>;
  public items$!: Observable<ServiceTypes>;

  public readonly stringify = (serviceTypes: ServiceTypes | null): (serviceType: ServiceType['name']) => string =>
    serviceType => serviceTypes?.find(({ name }) => name === serviceType)?.presentedName || '';

  public readonly matcher = ({ presentedName }: ServiceType): string => presentedName;

  public readonly content = (serviceType: ServiceType['name'], serviceTypes: ServiceTypes | null): string | undefined =>
    serviceTypes?.find(({ name }) => name === serviceType)?.presentedName || '';

  public readonly identity = (serviceType: ServiceType): ServiceType['name'] => serviceType.name;

  @Input() public readonly: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;
  @Input() public placeholder: Maybe<string>;
  @Input() public available: Maybe<readonly PackageCategory[]>;
  @Input() public excluded: Maybe<readonly PackageCategory[]>;
  @Input() public multi: Maybe<boolean>;
  @Input() public clearable = true;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<PackageCategory | null>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  public ngOnInit(): void {
    this.initQuery();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('available' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch().valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data.serviceTypes),
      map(items => this.available ? items.filter(({ name }) => this.available!.includes(name)) : items),
      map(items => this.excluded ? items.filter(({ name }) => !this.excluded!.includes(name)) : items),
    );
  }
}
